import type { Vendor, VendorArchiveImpact } from '@/types/vendor';

// archivedAt may be absent: a backend older than the archive feature omits the field entirely.
export function isArchived(vendor: { archivedAt?: string | null }): boolean {
  return vendor.archivedAt !== null && vendor.archivedAt !== undefined;
}

// `||`, not `??`: an empty alias falls back to the name, as everywhere else in the vendor views.
export function vendorDisplayName(vendor: Pick<Vendor, 'alias' | 'name'>): string {
  return vendor.alias || vendor.name;
}

export function vendorOptionLabel(vendor: Pick<Vendor, 'alias' | 'name'> & { archivedAt?: string | null }): string {
  return isArchived(vendor) ? `${vendorDisplayName(vendor)} (archived)` : vendorDisplayName(vendor);
}

// Keeps an already-selected archived vendor visible in a picker that otherwise lists only active ones.
export function vendorPickerOptions<T extends { id: string }>(active: T[], all: T[], selectedId: string | null): T[] {
  if (selectedId === null || active.some((vendor) => vendor.id === selectedId)) return active;
  const selected = all.find((vendor) => vendor.id === selectedId);
  return selected ? [...active, selected] : active;
}

// Thrown by the store when an Odoo vendor being added already exists archived; the view links to it.
export class ArchivedVendorError extends Error {
  constructor(readonly vendor: Vendor) {
    super(`${vendorDisplayName(vendor)} already exists and is archived — unarchive it`);
    this.name = 'ArchivedVendorError';
  }
}

const LOCALE = 'en-US';
const SINGLE = 1;

const countOf = (count: number, singular: string, plural: string): string => `${count} ${count === SINGLE ? singular : plural}`;

const PLAIN_ARCHIVE_CONFIRM = (label: string): string =>
  `Archive ${label}? It will be hidden from the board, pickers and imports. Its history stays, and you can unarchive it at any time.`;

export function buildArchiveConfirmMessage(label: string, impact: VendorArchiveImpact | null): string {
  if (impact === null) return `${PLAIN_ARCHIVE_CONFIRM(label)}\n\nPlanned work couldn't be checked.`;
  if (impact.plannedAssignmentCount === 0) return PLAIN_ARCHIVE_CONFIRM(label);

  const cards = countOf(impact.plannedAssignmentCount, 'card', 'cards');
  const planned =
    impact.plannedPoCount > 0
      ? `${countOf(impact.plannedPoCount, 'PO', 'POs')} planned from this week on (${cards})`
      : `${cards} planned from this week on`;
  return `${label} still has ${planned}. They'll be hidden with the vendor and come back if you unarchive it.`;
}

// UTC so the date doesn't shift across the day boundary for viewers west of UTC.
export function formatArchivedOn(vendor: { archivedAt?: string | null; archivedBy?: string | null }): string {
  if (!vendor.archivedAt) return '';
  const date = new Date(vendor.archivedAt).toLocaleDateString(LOCALE, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return vendor.archivedBy ? `Archived on ${date} by ${vendor.archivedBy}` : `Archived on ${date}`;
}
